import React, { useEffect, useRef, useState } from 'react';
import {
  MessageSquare,
  ChevronUp,
  ChevronDown,
  Eye,
  Bookmark,
  Share2,
  Award,
  ThumbsUp,
  Layers,
} from 'lucide-react';
import './FeatureShowcase.css';

/**
 * FeatureShowcase - Communities Feature Section
 * 3 distinct slide types: Detailed Thread, Replies, Communities
 */
const FeatureShowcase = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-rotate slides
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % 3);
    }, 4500);
    return () => clearInterval(interval);
  }, [isVisible]);

  // ========================================
  // SLIDE 1: Detailed Thread Data
  // ========================================
  const detailedThread = {
    community: { name: 'AI Safety Research', avatar: 'AS', color: '#f97316' },
    author: { name: 'Dr. Lena Hoffmann', role: 'creator', avatar: 'LH' },
    date: '2 hours ago',
    title: 'Breakthrough: Novel Alignment Technique Reduces Reward Hacking by 73%',
    content: `Our team just completed a 6-month study on reward model alignment. We found that combining constitutional AI principles with iterative refinement significantly reduces reward hacking behaviors.

Key findings:
• 73% reduction in reward hacking across benchmark tasks
• Improved generalization to out-of-distribution scenarios
• Maintained 94% of base model capabilities

Full paper and code will be released next week. Looking for collaborators to extend this to multimodal models.`,
    tags: ['Alignment', 'Safety', 'Research', 'Breakthrough'],
    awards: [
      { icon: '🏆', label: 'Gold', count: 3 },
      { icon: '💡', label: 'Insight', count: 7 },
      { icon: '🔬', label: 'Research', count: 2 },
    ],
    votes: 847,
    replies: 156,
    views: '3.2K',
  };

  // ========================================
  // SLIDE 2: Thread with Replies
  // ========================================
  const threadWithReplies = {
    title: 'Question: Best practices for fine-tuning LLMs on domain-specific data?',
    author: { name: 'Farid Anwar', avatar: 'FA' },
    replies: [
      {
        author: { name: 'Dr. Lena Hoffmann', avatar: 'LH', role: 'creator' },
        content: 'Great question! From our experience, the key is data quality over quantity. We use a 3-stage approach: 1) Domain corpus curation, 2) Synthetic data augmentation, 3) Human feedback loop.',
        votes: 234,
        time: '1h ago',
      },
      {
        author: { name: 'Ingrid Svensson', avatar: 'IS', role: 'moderator' },
        content: 'Adding to Lena\'s point - make sure to maintain a diverse validation set from your target domain. We saw 15% improvement just by fixing data leakage issues.',
        votes: 156,
        time: '45m ago',
      },
      {
        author: { name: 'Owen Gallagher', avatar: 'OG', role: 'member' },
        content: 'Has anyone tried LoRA for domain adaptation? We\'re seeing promising results with much lower compute requirements.',
        votes: 89,
        time: '20m ago',
      },
    ],
  };

  // ========================================
  // SLIDE 3: Discussion Thread Card (text-only)
  // ========================================
  const discussionThread = {
    community: { name: 'Computational Biology', avatar: 'CB', color: '#f97316' },
    author: { name: 'Prof. Wei Zhang', avatar: 'WZ', role: 'creator' },
    date: '4 hours ago',
    title: 'Should we standardize benchmark datasets for protein folding validation?',
    content: 'Current benchmarks are fragmented across labs. I propose a unified validation framework that includes both structural accuracy metrics and functional predictions. Our lab has drafted an initial specification.',
    votes: 312,
    replies: 47,
    views: '1.8K',
    threadReplies: [
      {
        author: { name: 'Dr. Clara Whitfield', avatar: 'CW', role: 'moderator' },
        content: 'Strongly agree. We wasted 3 months reproducing results because different teams used incompatible evaluation criteria. A shared benchmark would accelerate the whole field.',
        votes: 89,
        time: '3h ago',
        isHighlighted: true,
      },
      {
        author: { name: 'Tobias Richter', avatar: 'TR', role: 'member' },
        content: 'The challenge is getting consensus on metrics. RMSD alone isn\'t sufficient — we need to include binding affinity and stability predictions. Happy to contribute our evaluation pipeline.',
        votes: 64,
        time: '2h ago',
      },
    ],
    moreReplies: 45,
  };

  // ========================================
  // RENDER: Slide 1 - Detailed Thread
  // ========================================
  const renderDetailedThread = () => (
    <div className="fshow__slide fshow__slide--thread">
      {/* Header */}
      <div className="fshow__thread-header">
        <div className="fshow__thread-community" style={{ background: `${detailedThread.community.color}15`, color: detailedThread.community.color }}>
          <span className="fshow__thread-avatar" style={{ background: detailedThread.community.color }}>
            {detailedThread.community.avatar}
          </span>
          {detailedThread.community.name}
        </div>
        <span className="fshow__thread-meta">
          <span className="fshow__author-name">{detailedThread.author.name}</span>
          <Award size={10} className="fshow__author-badge" />
          <span className="fshow__thread-date">· {detailedThread.date}</span>
        </span>
      </div>

      {/* Title */}
      <h3 className="fshow__thread-title">{detailedThread.title}</h3>

      {/* Content */}
      <div className="fshow__thread-content">
        {detailedThread.content.split('\n\n').map((para, i) => (
          <p key={i}>{para}</p>
        ))}
      </div>

      {/* Tags */}
      <div className="fshow__thread-tags">
        {detailedThread.tags.map((tag, i) => (
          <span key={i} className="fshow__thread-tag"># {tag}</span>
        ))}
      </div>

      {/* Awards */}
      <div className="fshow__thread-awards">
        {detailedThread.awards.map((award, i) => (
          <span key={i} className="fshow__award-badge">
            {award.icon} {award.count}
          </span>
        ))}
      </div>

      {/* Footer Stats */}
      <div className="fshow__thread-footer">
        <div className="fshow__thread-votes">
          <ChevronUp size={14} />
          <span>{detailedThread.votes}</span>
          <ChevronDown size={14} />
        </div>
        <span className="fshow__thread-stat"><MessageSquare size={12} /> {detailedThread.replies}</span>
        <span className="fshow__thread-stat"><Eye size={12} /> {detailedThread.views}</span>
        <span className="fshow__thread-stat"><Bookmark size={12} /> Save</span>
        <span className="fshow__thread-stat"><Share2 size={12} /> Share</span>
      </div>
    </div>
  );

  // ========================================
  // RENDER: Slide 2 - Thread Replies
  // ========================================
  const renderRepliesView = () => (
    <div className="fshow__slide fshow__slide--replies">
      {/* Thread Header */}
      <div className="fshow__replies-header">
        <h3 className="fshow__replies-title">{threadWithReplies.title}</h3>
        <span className="fshow__replies-by">
          Asked by <strong>{threadWithReplies.author.name}</strong>
        </span>
      </div>

      {/* Replies Chain */}
      <div className="fshow__replies-list">
        {threadWithReplies.replies.map((reply, i) => (
          <div key={i} className="fshow__reply">
            <div className="fshow__reply-avatar" style={{ 
              background: reply.role === 'creator' ? '#374151' : 
                         reply.role === 'moderator' ? '#f97316' : '#9ca3af' 
            }}>
              {reply.author.avatar}
            </div>
            <div className="fshow__reply-body">
              <div className="fshow__reply-header">
                <span className="fshow__reply-author">{reply.author.name}</span>
                {reply.role === 'creator' && <span className="fshow__reply-badge creator"><Award size={9} /></span>}
                {reply.role === 'moderator' && <span className="fshow__reply-badge mod">MOD</span>}
                <span className="fshow__reply-time">{reply.time}</span>
              </div>
              <p className="fshow__reply-content">{reply.content}</p>
              <div className="fshow__reply-actions">
                <span className="fshow__reply-votes"><ThumbsUp size={11} /> {reply.votes}</span>
                <span className="fshow__reply-action">Reply</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ========================================
  // RENDER: Slide 3 - Discussion Thread Card
  // ========================================
  const renderDiscussionThread = () => (
    <div className="fshow__slide fshow__slide--discussion">
      {/* Thread Post */}
      <div className="fshow__disc-post">
        {/* Header Row */}
        <div className="fshow__disc-header">
          <div className="fshow__disc-community" style={{ background: `${discussionThread.community.color}12`, color: discussionThread.community.color }}>
            <span className="fshow__disc-comm-avatar" style={{ background: discussionThread.community.color }}>
              {discussionThread.community.avatar}
            </span>
            {discussionThread.community.name}
          </div>
          <span className="fshow__disc-date">{discussionThread.date}</span>
        </div>

        {/* Author */}
        <div className="fshow__disc-author-row">
          <span className="fshow__disc-author-avatar" style={{ background: discussionThread.community.color }}>
            {discussionThread.author.avatar}
          </span>
          <span className="fshow__disc-author-name">{discussionThread.author.name}</span>
          <Award size={10} className="fshow__disc-author-badge" />
        </div>

        {/* Post Content */}
        <h3 className="fshow__disc-title">{discussionThread.title}</h3>
        <p className="fshow__disc-content">{discussionThread.content}</p>

        {/* Post Stats */}
        <div className="fshow__disc-stats">
          <span className="fshow__disc-stat-votes">
            <ChevronUp size={13} />
            <span>{discussionThread.votes}</span>
          </span>
          <span className="fshow__disc-stat"><MessageSquare size={11} /> {discussionThread.replies}</span>
          <span className="fshow__disc-stat"><Eye size={11} /> {discussionThread.views}</span>
        </div>
      </div>

      {/* Replies Section */}
      <div className="fshow__disc-replies">
        <div className="fshow__disc-replies-label">
          <MessageSquare size={11} />
          <span>Top Replies</span>
        </div>

        {discussionThread.threadReplies.map((reply, i) => (
          <div key={i} className={`fshow__disc-reply ${reply.isHighlighted ? 'fshow__disc-reply--highlighted' : ''}`}>
            <div className="fshow__disc-reply-avatar" style={{
              background: reply.role === 'moderator' ? '#f97316' : '#94a3b8'
            }}>
              {reply.author.avatar}
            </div>
            <div className="fshow__disc-reply-body">
              <div className="fshow__disc-reply-meta">
                <span className="fshow__disc-reply-name">{reply.author.name}</span>
                {reply.role === 'moderator' && <span className="fshow__disc-reply-badge">MOD</span>}
                <span className="fshow__disc-reply-time">{reply.time}</span>
              </div>
              <p className="fshow__disc-reply-text">{reply.content}</p>
              <div className="fshow__disc-reply-actions">
                <span className="fshow__disc-reply-vote"><ThumbsUp size={10} /> {reply.votes}</span>
                <span className="fshow__disc-reply-action">Reply</span>
              </div>
            </div>
          </div>
        ))}

        {/* More replies indicator */}
        <div className="fshow__disc-more">
          View {discussionThread.moreReplies} more replies →
        </div>
      </div>
    </div>
  );

  // Slides array for rendering
  const slides = [
    { render: renderDetailedThread },
    { render: renderRepliesView },
    { render: renderDiscussionThread },
  ];

  return (
    <section
      ref={sectionRef}
      className={`fshow ${isVisible ? 'fshow--visible' : ''}`}
    >
      <div className="fshow__container">
        {/* Left - Text (Cursor-style clean formatting) */}
        <div className="fshow__text">
          <h2 className="fshow__title">Where ideas converge</h2>
          <p className="fshow__desc">
            Join research communities. Share findings, debate theories, find collaborators.
          </p>

          <a href="/communities" className="fshow__link">
            Explore Communities <span className="fshow__arrow">→</span>
          </a>

          {/* Feature highlights */}
          <div className="fshow__highlights">
            <div className="fshow__highlight">
              <div className="fshow__highlight-icon">
                <MessageSquare size={15} />
              </div>
              <div className="fshow__highlight-content">
                <span className="fshow__highlight-title">Threaded Discussions</span>
                <span className="fshow__highlight-desc">Deep-dive into topics with structured, votable threads</span>
              </div>
            </div>
            <div className="fshow__highlight">
              <div className="fshow__highlight-icon">
                <Award size={15} />
              </div>
              <div className="fshow__highlight-content">
                <span className="fshow__highlight-title">Peer Recognition</span>
                <span className="fshow__highlight-desc">Award insightful contributions and build reputation</span>
              </div>
            </div>
            <div className="fshow__highlight">
              <div className="fshow__highlight-icon">
                <Eye size={15} />
              </div>
              <div className="fshow__highlight-content">
                <span className="fshow__highlight-title">Real-time Activity</span>
                <span className="fshow__highlight-desc">See live replies, votes, and trending discussions</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right - Rotating Slides */}
        <div className="fshow__cards">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`fshow__card ${index === activeSlide ? 'fshow__card--active' : ''} ${
                index < activeSlide ? 'fshow__card--prev' : ''
              } ${index > activeSlide ? 'fshow__card--next' : ''}`}
            >
              {slide.render()}
            </div>
          ))}

          {/* Slide Indicators */}
          <div className="fshow__indicators">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`fshow__indicator ${i === activeSlide ? 'fshow__indicator--active' : ''}`}
                onClick={() => setActiveSlide(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureShowcase;
